import React, { useState, useRef, useEffect } from "react";
import styles from "./ProductSelectionDrawer.module.css";

interface FilterDropdownProps {
	label: string;
	options: string[];
	selectedValues: string[];
	onSelect: (values: string[]) => void;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
	label,
	options,
	selectedValues,
	onSelect,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		setIsOpen(!isOpen);
	};

	const handleSelect = (option: string) => {
		const newValues = selectedValues.includes(option)
			? selectedValues.filter((v) => v !== option)
			: [...selectedValues, option];
		onSelect(newValues);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const displayLabel = selectedValues.length > 0
		? `${label} (${selectedValues.length})`
		: label;

	return (
		<div className={styles.filterButtonWrapper} ref={dropdownRef}>
			<button
				className={styles.filterButton}
				onClick={handleToggle}
				aria-expanded={isOpen}
				aria-haspopup="true"
			>
				{displayLabel}
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="currentColor"
					className={styles.chevron}
					aria-hidden="true"
				>
					<path
						d="M4 6l4 4 4-4"
						stroke="currentColor"
						strokeWidth="2"
						fill="none"
					/>
				</svg>
			</button>
			{isOpen && (
				<div className={styles.filterDropdown}>
					{options.map((option) => (
						<label key={option} className={styles.filterOption}>
							<input
								type="checkbox"
								checked={selectedValues.includes(option)}
								onChange={() => handleSelect(option)}
							/>
							<span>{option}</span>
						</label>
					))}
				</div>
			)}
		</div>
	);
};

interface ActiveFiltersProps {
	filters: string[];
	onClear: (filter: string) => void;
}

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
	filters,
	onClear,
}) => {
	if (filters.length === 0) return null;

	return (
		<div className={styles.activeFilters}>
			{filters.map((filter) => (
				<span key={filter} className={styles.activeFilterTag}>
					{filter}
					<button
						className={styles.clearFilter}
						onClick={() => onClear(filter)}
						aria-label="Clear filter"
					>
						×
					</button>
				</span>
			))}
		</div>
	);
};

interface FilterSectionProps {
	title?: string;
	children: React.ReactNode;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
	title = "Filtrer produkter",
	children,
}) => {
	return (
		<div className={styles.filterSection}>
			<h3 className={styles.filterTitle}>{title}</h3>
			<div className={styles.filterButtons}>
				{children}
			</div>
		</div>
	);
};